/**
 * ReceivedQuoteRequestsPage
 * Dashboard for contractors and verifiers to view and manage incoming quote requests.
 * Includes ability to respond with pricing and manage negotiations.
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Chip,
  Button,
  LinearProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Avatar,
  Stack,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField
} from '@mui/material';
import {
  Send,
  CheckCircle,
  HourglassEmpty,
  Cancel,
  Visibility,
  Person,
  AttachMoney,
  LocationOn,
  Refresh,
  SwapHoriz,
  Reply,
  Assignment,
  Message,
  EventAvailable
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { formatHarareWindow, harareInputToIso, isoToHarareInput, windowStart } from '../utils/time';
import { brand, text, border, bg } from '../theme/colors';
import QuoteResponseDialog from '../components/quotes/QuoteResponseDialog';
import CounterOfferDialog from '../components/quotes/CounterOfferDialog';
import QuoteAttachments from '../components/quotes/QuoteAttachments';
import NegotiationTimeline from '../components/quotes/NegotiationTimeline';
import DialogCloseButton from '../components/common/DialogCloseButton';
import { renderMarkdown } from '../components/documents/documentStyles';
import useProfileGate from '../hooks/useProfileGate';
import ProfileGateDialog from '../components/ProfileGateDialog';

const ReceivedQuoteRequestsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { checkAction, showGateDialog, gateAction, closeGateDialog, profileData } = useProfileGate();

  const [quoteRequests, setQuoteRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Dialog states
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [responseDialogOpen, setResponseDialogOpen] = useState(false);
  const [counterOfferDialogOpen, setCounterOfferDialogOpen] = useState(false);
  const [chosenVisitWindow, setChosenVisitWindow] = useState('');
  const [confirmingVisit, setConfirmingVisit] = useState(false);
  const [showRescheduleEditor, setShowRescheduleEditor] = useState(false);
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [rescheduling, setRescheduling] = useState(false);
  const [showCompleteVisitEditor, setShowCompleteVisitEditor] = useState(false);
  const [visitPhotos, setVisitPhotos] = useState([]);
  const [completingVisit, setCompletingVisit] = useState(false);

  const parseVisitWindows = (request) => {
    const raw = request?.proposed_visit_windows;
    if (Array.isArray(raw)) return raw;
    try { return JSON.parse(raw || '[]'); } catch { return []; }
  };

  // A quote-request time can be a site visit or a client call (null = legacy visit).
  const isCallRequest = (request) => request?.visit_type === 'client_call';
  const qrVisitLabel = (request) => (isCallRequest(request) ? 'Client call' : 'Site visit');

  // The owner proposed site-visit times and is waiting for this provider to
  // confirm one — surfaced as a list badge so it isn't buried in the detail dialog.
  const hasPendingClientVisit = (request) => (
    request?.visit_proposed_by === 'client'
    && parseVisitWindows(request).length > 0
    && !request?.accepted_visit_window
    && ['pending', 'responded', 'negotiating'].includes(request?.status)
  );

  // Visit times render in the property's timezone (Harare / CAT) so the
  // contractor sees the same labelled number the client picked.
  const formatVisitWindow = formatHarareWindow;

  // accepted_visit_window stores only the start; recover the full window
  // (with its end time) from the proposed list for display.
  const formatAcceptedVisit = (request) => {
    const accepted = request.accepted_visit_window;
    const match = parseVisitWindows(request).find(
      (w) => new Date(windowStart(w)).getTime() === new Date(accepted).getTime()
    );
    return formatVisitWindow(match || accepted);
  };

  // Mark the confirmed visit/call complete. Physical visits need proof photos
  // taken at the site — the (often remote) owner gets notified with them.
  const handleCompleteVisit = async () => {
    setCompletingVisit(true);
    try {
      const formData = new FormData();
      visitPhotos.forEach((photo) => formData.append('files', photo));
      const response = await api.post(
        `/quote-requests/${selectedRequest.id}/complete-visit`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (response.data.success) {
        setSelectedRequest((prev) => ({ ...prev, ...response.data.data.quoteRequest }));
        setShowCompleteVisitEditor(false);
        setVisitPhotos([]);
        loadQuoteRequests();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to mark the visit complete');
    } finally {
      setCompletingVisit(false);
    }
  };

  // One-step reschedule of a confirmed visit/call — records what was
  // re-agreed in chat and notifies the owner. No re-propose loop.
  const handleRescheduleVisit = async () => {
    if (!rescheduleTime) return;
    setRescheduling(true);
    try {
      const response = await api.put(`/quote-requests/${selectedRequest.id}/reschedule-visit`, {
        newTime: harareInputToIso(rescheduleTime)
      });
      if (response.data.success) {
        setSelectedRequest((prev) => ({ ...prev, ...response.data.data.quoteRequest }));
        setShowRescheduleEditor(false);
        setRescheduleTime('');
        loadQuoteRequests();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reschedule');
    } finally {
      setRescheduling(false);
    }
  };

  // Provider confirms one of the CLIENT's proposed visit times.
  const handleConfirmVisit = async () => {
    if (!chosenVisitWindow) return;
    setConfirmingVisit(true);
    try {
      const response = await api.put(`/quote-requests/${selectedRequest.id}/accept-visit`, {
        visitWindow: chosenVisitWindow
      });
      if (response.data.success) {
        setSelectedRequest((prev) => ({ ...prev, ...response.data.data.quoteRequest }));
        setChosenVisitWindow('');
        loadQuoteRequests();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to confirm site visit');
    } finally {
      setConfirmingVisit(false);
    }
  };

  useEffect(() => {
    loadQuoteRequests();
  }, []);

  // Never carry a half-open reschedule editor from one request to another.
  useEffect(() => {
    setShowRescheduleEditor(false);
    setRescheduleTime('');
  }, [selectedRequest?.id]);

  // Deep-link from a "site-visit times proposed" notification/email:
  // /received-quotes?request=<id> opens that request straight to its details.
  useEffect(() => {
    const rid = searchParams.get('request');
    if (!rid || !quoteRequests.length) return;
    const match = quoteRequests.find((q) => String(q.id) === String(rid));
    if (match) {
      setSelectedRequest(match);
      setDetailDialogOpen(true);
    }
  }, [searchParams, quoteRequests]);

  const loadQuoteRequests = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/quote-requests');
      setQuoteRequests(response.data.data?.quoteRequests || []);
    } catch (err) {
      console.error('Error loading quote requests:', err);
      setError('Failed to load quote requests');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (request) => {
    if (!checkAction('bid')) return;

    try {
      await api.put(`/quote-requests/${request.id}/accept`);
      loadQuoteRequests();
    } catch (err) {
      console.error('Accept error:', err);
      setError(err.response?.data?.message || 'Failed to accept quote');
    }
  };

  const openResponseDialog = (request) => {
    if (!checkAction('bid')) return;
    setSelectedRequest(request);
    setResponseDialogOpen(true);
  };

  const openCounterOfferDialog = (request) => {
    if (!checkAction('bid')) return;
    setSelectedRequest(request);
    setCounterOfferDialogOpen(true);
  };

  const handleDecline = async (request) => {
    try {
      await api.put(`/quote-requests/${request.id}/decline`);
      loadQuoteRequests();
    } catch (err) {
      console.error('Decline error:', err);
      setError(err.response?.data?.message || 'Failed to decline quote');
    }
  };

  const getStatusChipSx = (status) => {
    switch (status) {
      case 'pending':
        return { bgcolor: '#fffbeb', color: '#FAA61A', border: '1px solid #fde68a' };
      case 'responded':
        return { bgcolor: '#eff6ff', color: brand.primary, border: '1px solid #bfdbfe' };
      case 'negotiating':
        return { bgcolor: '#fffbeb', color: '#f59e0b', border: '1px solid #fde68a' };
      case 'accepted':
        return { bgcolor: '#f0fdf4', color: '#43B581', border: '1px solid #bbf7d0' };
      case 'declined':
      case 'cancelled':
        return { bgcolor: '#fdf2f2', color: '#e74c3c', border: '1px solid #fecaca' };
      default:
        return {};
    }
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <HourglassEmpty fontSize="small" />,
      responded: <Send fontSize="small" />,
      negotiating: <SwapHoriz fontSize="small" />,
      accepted: <CheckCircle fontSize="small" />,
      declined: <Cancel fontSize="small" />,
      cancelled: <Cancel fontSize="small" />
    };
    return icons[status];
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(amount);
  };

  const cleanDisplayName = (value) => {
    if (!value || typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) return null;
    return trimmed;
  };

  const getClientDisplayName = (request) => (
    cleanDisplayName(request.client_display_name)
    || cleanDisplayName(request.client_name)
    || 'Client'
  );

  const goToMyWork = () => {
    navigate('/verifier/my-work');
  };

  const getProviderRole = (request) => (
    request.target_type === 'verifier' ? 'verifier' : 'contractor'
  );

  const isProviderTurn = (request) => {
    const latestOfferRole = request.latest_offer_role
      || (request.status === 'responded' ? getProviderRole(request) : null);

    return request.status === 'negotiating'
      && latestOfferRole
      && latestOfferRole !== getProviderRole(request);
  };

  const filteredRequests = quoteRequests.filter(req =>
    statusFilter === 'all' || req.status === statusFilter
  );

  const stats = {
    total: quoteRequests.length,
    pending: quoteRequests.filter(r => r.status === 'pending').length,
    responded: quoteRequests.filter(r => r.status === 'responded').length,
    negotiating: quoteRequests.filter(r => r.status === 'negotiating').length,
    accepted: quoteRequests.filter(r => r.status === 'accepted').length
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Received Quote Requests
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage incoming quote requests from clients
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={loadQuoteRequests}
          sx={{ borderColor: brand.primary, color: brand.primary, borderWidth: 2, '&:hover': { backgroundColor: brand.primary, color: 'white' } }}
        >
          Refresh
        </Button>
      </Box>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Received', value: stats.total, color: brand.primary },
          { label: 'New (Pending)', value: stats.pending, color: '#FAA61A' },
          { label: 'Responded', value: stats.responded, color: '#2196f3' },
          { label: 'Negotiating', value: stats.negotiating, color: '#f59e0b' },
          { label: 'Accepted', value: stats.accepted, color: '#43B581' }
        ].map((stat, i) => (
          <Grid item xs={6} sm={2.4} key={i}>
            <Paper elevation={0} sx={{ p: 2, textAlign: 'center', bgcolor: 'white', border: `2px solid ${border.light}`, borderRadius: 2 }}>
              <Typography variant="h4" fontWeight={700} sx={{ color: stat.color }}>
                {stat.value}
              </Typography>
              <Typography variant="caption" sx={{ color: text.secondary }}>{stat.label}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Status Filter */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {['all', 'pending', 'responded', 'negotiating', 'accepted', 'declined'].map((status) => (
          <Chip
            key={status}
            label={status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
            onClick={() => setStatusFilter(status)}
            color={statusFilter === status ? 'primary' : 'default'}
            variant={statusFilter === status ? 'filled' : 'outlined'}
          />
        ))}
      </Box>

      {/* Loading & Error */}
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Table */}
      {!loading && filteredRequests.length === 0 && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No quote requests found. When clients send you quote requests, they'll appear here.
        </Alert>
      )}

      {!loading && filteredRequests.length > 0 && (
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: bg.subtle }}>
                <TableCell><strong>Client</strong></TableCell>
                <TableCell><strong>Project Description</strong></TableCell>
                <TableCell><strong>Location</strong></TableCell>
                <TableCell><strong>Client Budget</strong></TableCell>
                <TableCell><strong>Your Quote</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Received</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((request) => (
                <TableRow key={request.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar sx={{ bgcolor: brand.primary, width: 32, height: 32 }}>
                        <Person fontSize="small" />
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {getClientDisplayName(request)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {request.client_email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {request.project_description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2">{request.project_location || '-'}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {formatCurrency(request.estimated_budget, request.currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600} sx={{ color: request.quote_amount ? brand.primary : 'text.secondary' }}>
                      {formatCurrency(request.quote_amount, request.quote_currency)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={request.status?.toUpperCase()}
                      size="small"
                      icon={getStatusIcon(request.status)}
                      sx={getStatusChipSx(request.status)}
                    />
                    {hasPendingClientVisit(request) && (
                      <Chip
                        label={isCallRequest(request) ? "Confirm call times" : "Confirm visit times"}
                        size="small"
                        icon={<EventAvailable fontSize="small" />}
                        onClick={() => { setSelectedRequest(request); setDetailDialogOpen(true); }}
                        sx={{
                          mt: 0.5,
                          display: 'flex',
                          cursor: 'pointer',
                          bgcolor: '#ecfdf5',
                          color: '#0f766e',
                          border: '1px solid #99f6e4',
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: '#0f766e' }
                        }}
                      />
                    )}
                    {request.accepted_visit_window && ['pending', 'responded', 'negotiating'].includes(request.status) && (
                      <Chip
                        label={request.visit_completed_at
                          ? (isCallRequest(request) ? 'Call completed' : 'Visit completed')
                          : (isCallRequest(request) ? 'Call confirmed' : 'Visit confirmed')}
                        size="small"
                        icon={<EventAvailable fontSize="small" />}
                        onClick={() => { setSelectedRequest(request); setDetailDialogOpen(true); }}
                        sx={{
                          mt: 0.5,
                          display: 'flex',
                          cursor: 'pointer',
                          bgcolor: '#f0fdf4',
                          color: '#15803d',
                          border: '1px solid #bbf7d0',
                          fontWeight: 600,
                          '& .MuiChip-icon': { color: '#15803d' }
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(request.created_at)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      {/* View Details */}
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedRequest(request);
                            setDetailDialogOpen(true);
                          }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      {/* Respond (pending only) */}
                      {request.status === 'pending' && (
                        <Tooltip title="Respond with Quote">
                          <IconButton
                            size="small"
                            sx={{ color: brand.primary }}
                            onClick={() => openResponseDialog(request)}
                          >
                            <Reply fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Counter-offer (negotiating) */}
                      {isProviderTurn(request) && (
                        <Tooltip title="Make Counter-Offer">
                          <IconButton
                            size="small"
                            sx={{ color: '#f59e0b' }}
                            onClick={() => {
                              setSelectedRequest(request);
                              setCounterOfferDialogOpen(true);
                            }}
                          >
                            <SwapHoriz fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {request.status === 'accepted' && request.target_type === 'verifier' && (
                        <Tooltip title="Go to My Work">
                          <IconButton
                            size="small"
                            sx={{ color: '#43B581' }}
                            onClick={goToMyWork}
                          >
                            <Assignment fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => { setDetailDialogOpen(false); setSelectedRequest(null); }}
        maxWidth="md"
        fullWidth
      >
        <DialogCloseButton onClick={() => { setDetailDialogOpen(false); setSelectedRequest(null); }} />
        {selectedRequest && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoney sx={{ color: brand.primary }} />
              Quote Request Details
            </DialogTitle>
            <DialogContent dividers>
              <Stack spacing={2.5}>
                {/* The page-level error banner sits BEHIND this dialog — failures
                    must also surface here or actions look like they did nothing. */}
                {error && (
                  <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>
                )}

                {/* Client Info */}
                <Box>
                  <Typography variant="overline" color="text.secondary">Client</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 0.5 }}>
                    <Avatar sx={{ bgcolor: brand.primary }}><Person /></Avatar>
                    <Box>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {getClientDisplayName(selectedRequest)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">{selectedRequest.client_email}</Typography>
                    </Box>
                  </Box>
                </Box>

                <Divider />

                {/* Project Info */}
                <Box>
                  <Typography variant="overline" color="text.secondary">Project Description</Typography>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, mt: 0.5, bgcolor: bg.subtle, maxHeight: 400, overflowY: 'auto' }}
                  >
                    {renderMarkdown(selectedRequest.project_description)}
                  </Paper>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="text.secondary">Location</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                      <LocationOn fontSize="small" color="action" />
                      <Typography variant="body2">{selectedRequest.project_location || '-'}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="text.secondary">Client Budget</Typography>
                    <Typography variant="body1" fontWeight={600} sx={{ mt: 0.5 }}>
                      {formatCurrency(selectedRequest.estimated_budget, selectedRequest.currency)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="text.secondary">Status</Typography>
                    <Box sx={{ mt: 0.5 }}>
                      <Chip
                        label={selectedRequest.status?.toUpperCase()}
                        size="small"
                        icon={getStatusIcon(selectedRequest.status)}
                        sx={getStatusChipSx(selectedRequest.status)}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="overline" color="text.secondary">Received</Typography>
                    <Typography variant="body2" sx={{ mt: 0.5 }}>{formatDate(selectedRequest.created_at)}</Typography>
                  </Grid>
                </Grid>

                {selectedRequest.additional_notes && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="overline" color="text.secondary">Additional Notes</Typography>
                      <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: bg.subtle }}>
                        <Typography variant="body2">{selectedRequest.additional_notes}</Typography>
                      </Paper>
                    </Box>
                  </>
                )}

                {/* Your Response */}
                {selectedRequest.quote_amount && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="overline" color="text.secondary">Your Quote Response</Typography>
                      <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: '#f0fdf4' }}>
                        <Typography variant="h6" fontWeight={700} sx={{ color: brand.primary }}>
                          {formatCurrency(selectedRequest.quote_amount, selectedRequest.quote_currency)}
                        </Typography>
                        {selectedRequest.proposed_start_date && (
                          <Typography variant="body2" color="text.secondary">
                            Start: {formatDate(selectedRequest.proposed_start_date)}
                          </Typography>
                        )}
                        {selectedRequest.estimated_timeline_days && (
                          <Typography variant="body2" color="text.secondary">
                            Timeline: {selectedRequest.estimated_timeline_days} days
                          </Typography>
                        )}
                        {selectedRequest.response_message && (
                          <Typography variant="body2" sx={{ mt: 1 }}>{selectedRequest.response_message}</Typography>
                        )}
                      </Paper>
                    </Box>
                  </>
                )}

                {/* Site Visit — the client can now propose visit times too (with or
                    without a price on the table); confirm one of theirs here. Legacy
                    rows with windows but no visit_proposed_by are provider-proposed. */}
                {(() => {
                  const visitWindows = parseVisitWindows(selectedRequest);
                  const proposedBy = selectedRequest.visit_proposed_by
                    || (visitWindows.length > 0 ? 'provider' : null);
                  const clientProposedPending = proposedBy === 'client'
                    && visitWindows.length > 0
                    && !selectedRequest.accepted_visit_window
                    && ['pending', 'responded', 'negotiating'].includes(selectedRequest.status);
                  const providerWaiting = ['responded', 'negotiating'].includes(selectedRequest.status)
                    && !selectedRequest.quote_amount
                    && proposedBy !== 'client';
                  if (!clientProposedPending && !providerWaiting && !selectedRequest.accepted_visit_window) return null;
                  return (
                    <>
                      <Divider />
                      <Box>
                        <Typography variant="overline" color="text.secondary">{qrVisitLabel(selectedRequest)}</Typography>
                        <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: selectedRequest.accepted_visit_window ? '#f0fdf4' : '#fffbeb' }}>
                          {/* Call-out fee status — money moves through Eqaya, never cash on the day */}
                          {selectedRequest.visit_fee_amount && selectedRequest.visit_fee_status && (
                            <Alert
                              severity={selectedRequest.visit_fee_status === 'proposed' ? 'info' : 'success'}
                              sx={{ mb: 1.5 }}
                            >
                              {selectedRequest.visit_fee_status === 'proposed' && (
                                <>Your <strong>${Number(selectedRequest.visit_fee_amount).toFixed(2)} call-out fee</strong> is waiting for the owner to pay it through Eqaya. It will be held in escrow and released to your earnings once the {isCallRequest(selectedRequest) ? 'call' : 'visit'} is confirmed complete.</>
                              )}
                              {selectedRequest.visit_fee_status === 'held' && (
                                <>Your <strong>${Number(selectedRequest.visit_fee_amount).toFixed(2)} call-out fee is secured in escrow</strong> — it's released to your earnings when the {isCallRequest(selectedRequest) ? 'call' : 'visit'} is confirmed complete. Don't collect cash on the day.</>
                              )}
                              {selectedRequest.visit_fee_status === 'released' && (
                                <>Your <strong>${Number(selectedRequest.visit_fee_amount).toFixed(2)} call-out fee was released</strong> (minus the 3% platform fee) to your earnings — check your Financial Hub.</>
                              )}
                            </Alert>
                          )}
                          {selectedRequest.accepted_visit_window && selectedRequest.visit_completed_at ? (
                            <>
                              <Typography variant="body2" fontWeight={600} sx={{ color: '#15803d' }}>
                                {qrVisitLabel(selectedRequest)} completed — {formatAcceptedVisit(selectedRequest)}
                              </Typography>
                              <Typography variant="body2" sx={{ mt: 0.5 }}>
                                The owner has been notified
                                {Array.isArray(selectedRequest.visit_photo_urls) && selectedRequest.visit_photo_urls.length > 0
                                  ? ` and can see your ${selectedRequest.visit_photo_urls.length} site photo${selectedRequest.visit_photo_urls.length === 1 ? '' : 's'}`
                                  : ''}. Now send your price with "Respond with Quote" while the details are fresh.
                              </Typography>
                            </>
                          ) : selectedRequest.accepted_visit_window ? (
                            <>
                              <Typography variant="body2" fontWeight={600}>
                                {qrVisitLabel(selectedRequest)} confirmed: {formatAcceptedVisit(selectedRequest)}
                                {Number(selectedRequest.visit_rescheduled_count) > 0 && (
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                    (rescheduled {selectedRequest.visit_rescheduled_count}×)
                                  </Typography>
                                )}
                              </Typography>
                              {selectedRequest.visit_access_notes && (
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                  <strong>Access instructions from the owner:</strong> {selectedRequest.visit_access_notes}
                                </Typography>
                              )}
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                After the {isCallRequest(selectedRequest) ? 'call' : 'visit'}, come back here and send your price with "Respond with Quote".
                                {selectedRequest.visit_fee_amount
                                  ? ' Your call-out fee is handled through Eqaya — never collect cash on the day.'
                                  : ` ${isCallRequest(selectedRequest) ? 'Calls and site visits' : 'Site visits'} are free — never charge for them.`}
                              </Typography>
                              {showCompleteVisitEditor ? (
                                <Box sx={{ mt: 1.5 }}>
                                  <Typography variant="body2" sx={{ mb: 1 }}>
                                    {isCallRequest(selectedRequest)
                                      ? 'Confirm the call took place — the owner will be told to expect your quotation.'
                                      : 'Add at least one photo taken at the property — it shows the owner the visit happened.'}
                                  </Typography>
                                  {!isCallRequest(selectedRequest) && (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                                      <Button size="small" variant="outlined" component="label">
                                        Add Photos
                                        <input
                                          hidden
                                          multiple
                                          type="file"
                                          accept="image/*"
                                          onChange={(e) => setVisitPhotos(Array.from(e.target.files || []).slice(0, 5))}
                                        />
                                      </Button>
                                      {visitPhotos.map((photo) => (
                                        <Chip key={photo.name} label={photo.name} size="small" onDelete={() => setVisitPhotos((prev) => prev.filter((p) => p !== photo))} />
                                      ))}
                                    </Box>
                                  )}
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      disabled={completingVisit || (!isCallRequest(selectedRequest) && visitPhotos.length === 0)}
                                      onClick={handleCompleteVisit}
                                      sx={{ bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa370' } }}
                                    >
                                      {completingVisit ? 'Saving…' : `Confirm ${isCallRequest(selectedRequest) ? 'Call' : 'Visit'} Complete`}
                                    </Button>
                                    <Button size="small" onClick={() => { setShowCompleteVisitEditor(false); setVisitPhotos([]); }}>Cancel</Button>
                                  </Box>
                                </Box>
                              ) : !showRescheduleEditor ? (
                                <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                  {new Date(selectedRequest.accepted_visit_window).getTime() <= Date.now() && (
                                    <Button
                                      size="small"
                                      variant="contained"
                                      onClick={() => setShowCompleteVisitEditor(true)}
                                      sx={{ bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa370' } }}
                                    >
                                      {isCallRequest(selectedRequest) ? 'Confirm Call Complete' : 'Confirm Visit Complete'}
                                    </Button>
                                  )}
                                  <Button size="small" variant="outlined" onClick={() => { setRescheduleTime(isoToHarareInput(selectedRequest.accepted_visit_window)); setShowRescheduleEditor(true); }}>
                                    Reschedule
                                  </Button>
                                </Box>
                              ) : (
                                <Box sx={{ mt: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <TextField
                                    type="datetime-local"
                                    size="small"
                                    label="New time (Zimbabwe time)"
                                    value={rescheduleTime}
                                    onChange={(e) => setRescheduleTime(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                  />
                                  <Button size="small" variant="contained" disabled={!rescheduleTime || rescheduling} onClick={handleRescheduleVisit}>
                                    {rescheduling ? 'Saving…' : 'Save'}
                                  </Button>
                                  <Button size="small" onClick={() => setShowRescheduleEditor(false)}>Cancel</Button>
                                </Box>
                              )}
                            </>
                          ) : clientProposedPending ? (
                            <>
                              <Typography variant="body2" sx={{ mb: 1 }}>
                                <strong>{getClientDisplayName(selectedRequest)} proposed {isCallRequest(selectedRequest) ? 'client call' : 'site visit'} times.</strong> Confirm
                                one that works for you{isCallRequest(selectedRequest) ? '' : " — you'll get their access instructions once confirmed"}.
                                {' '}{isCallRequest(selectedRequest) ? 'Calls and site visits' : 'Site visits'} are free — never charge for them.
                              </Typography>
                              <RadioGroup value={chosenVisitWindow} onChange={(e) => setChosenVisitWindow(e.target.value)}>
                                {visitWindows.map((w) => (
                                  <FormControlLabel
                                    key={windowStart(w)}
                                    value={windowStart(w)}
                                    control={<Radio size="small" />}
                                    label={formatVisitWindow(w)}
                                  />
                                ))}
                              </RadioGroup>
                              <Button
                                variant="contained"
                                startIcon={<EventAvailable />}
                                disabled={!chosenVisitWindow || confirmingVisit}
                                onClick={handleConfirmVisit}
                                sx={{ mt: 1, bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa370' } }}
                              >
                                Confirm This Time
                              </Button>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                None of these work? Use Message Client below to suggest other times.
                              </Typography>
                            </>
                          ) : (
                            <Typography variant="body2">
                              You asked for a {isCallRequest(selectedRequest) ? 'client call' : 'site visit'} before pricing. Waiting for the owner to confirm one of your proposed times.
                            </Typography>
                          )}
                          {selectedRequest.response_message && (
                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                              "{selectedRequest.response_message}"
                            </Typography>
                          )}
                        </Paper>
                      </Box>
                    </>
                  );
                })()}

                {/* Attachments — plans, photos, documents shared inside the request */}
                <Divider />
                <QuoteAttachments quoteRequestId={selectedRequest.id} />

                {/* Negotiation Timeline */}
                {(selectedRequest.status === 'negotiating' || selectedRequest.negotiation_round > 0) && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="overline" color="text.secondary">Negotiation History</Typography>
                      <Box sx={{ mt: 1 }}>
                        <NegotiationTimeline
                          quoteRequestId={selectedRequest.id}
                          maxRounds={selectedRequest.max_negotiation_rounds || 5}
                          currentRound={selectedRequest.negotiation_round || 0}
                        />
                      </Box>
                    </Box>
                  </>
                )}
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => { setDetailDialogOpen(false); setSelectedRequest(null); }}>
                Close
              </Button>
              {selectedRequest.client_id && (
                <Button
                  variant="outlined"
                  startIcon={<Message />}
                  onClick={() => navigate(`/messages?userId=${selectedRequest.client_id}${selectedRequest.project_id ? `&projectId=${selectedRequest.project_id}` : ''}`)}
                >
                  Message Client
                </Button>
              )}
              {/* Pending, or priced-after-site-visit: a message-only response leaves
                  quote_amount null so the provider can come back and price the work. */}
              {(selectedRequest.status === 'pending'
                || (selectedRequest.status === 'responded' && !selectedRequest.quote_amount)) && (
                <Button
                  variant="contained"
                  startIcon={<Reply />}
                  onClick={() => {
                    setDetailDialogOpen(false);
                    openResponseDialog(selectedRequest);
                  }}
                  sx={{ bgcolor: brand.primary, '&:hover': { bgcolor: brand.primaryDark } }}
                >
                  {selectedRequest.status === 'pending' ? 'Respond with Quote' : 'Send Your Price'}
                </Button>
              )}
              {/* Already priced but the client hasn't acted yet: revise it. Ticking
                  "site visit first" in the dialog withdraws the price entirely —
                  the escape hatch when a price was entered just to move forward. */}
              {selectedRequest.status === 'responded' && Boolean(selectedRequest.quote_amount) && (
                <Button
                  variant="outlined"
                  startIcon={<Reply />}
                  onClick={() => {
                    setDetailDialogOpen(false);
                    openResponseDialog(selectedRequest);
                  }}
                >
                  Revise Response
                </Button>
              )}
              {isProviderTurn(selectedRequest) && (
                <>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => { handleDecline(selectedRequest); setDetailDialogOpen(false); setSelectedRequest(null); }}
                  >
                    Decline
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<SwapHoriz />}
                    onClick={() => {
                      setDetailDialogOpen(false);
                      openCounterOfferDialog(selectedRequest);
                    }}
                    sx={{ borderColor: '#f59e0b', color: '#f59e0b' }}
                  >
                    Counter-Offer
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<CheckCircle />}
                    onClick={() => { handleAccept(selectedRequest); setDetailDialogOpen(false); setSelectedRequest(null); }}
                    sx={{ bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa370' } }}
                  >
                    Accept
                  </Button>
                </>
              )}
              {selectedRequest.status === 'accepted' && selectedRequest.target_type === 'verifier' && (
                <Button
                  variant="contained"
                  startIcon={<Assignment />}
                  onClick={goToMyWork}
                  sx={{ bgcolor: '#43B581', '&:hover': { bgcolor: '#3aa370' } }}
                >
                  Go to My Work
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Quote Response Dialog */}
      <QuoteResponseDialog
        open={responseDialogOpen}
        onClose={() => { setResponseDialogOpen(false); setSelectedRequest(null); }}
        quoteRequest={selectedRequest}
        onSuccess={() => { loadQuoteRequests(); }}
      />

      {/* Counter-Offer Dialog */}
      <CounterOfferDialog
        open={counterOfferDialogOpen}
        onClose={() => { setCounterOfferDialogOpen(false); setSelectedRequest(null); }}
        quoteRequest={selectedRequest}
        onSuccess={() => { loadQuoteRequests(); }}
      />

      <ProfileGateDialog
        open={showGateDialog}
        onClose={closeGateDialog}
        action={gateAction}
        profileData={profileData}
      />
    </Container>
  );
};

export default ReceivedQuoteRequestsPage;
